'use client';

/**
 * app/help/_components/HelpChatClient.tsx
 *
 * Client Component — multi-turn AI chatbot for the /help page (Cycle 13).
 * Cream island on dark page per Aria C7 spec.
 *
 * Cycle 15 changes (2026-05-13):
 *   - B1: aiBadgeLabel renamed 'Piano Fantasy AI' → 'Eleanor AI Assistant'.
 *   - B3: Cloudflare Turnstile scaffold. Invisible widget injected when
 *         NEXT_PUBLIC_TURNSTILE_SITE_KEY is set at build time. Token passed
 *         as turnstileToken in the email-transcript request body. Falls back
 *         to rate-limit-only protection when the env var is unset (fail-open).
 *   - B3 (Cycle 16, 2026-05-13): turnstile.reset() on success path so
 *         multi-submit in same session gets a fresh token (single-use tokens).
 *   - B3 (Cycle 17, 2026-05-13): widget container changed from display:none to
 *         off-screen absolute positioning so the Turnstile iframe can render and
 *         solve. display:none removed the container from layout entirely, preventing
 *         the challenge from initialising and causing getResponse() to return ''.
 *
 * Cycle 13 changes:
 *   - B1: welcomePulse bounce animation removed entirely. Typewriter only on welcome.
 *         welcomeAnimActive state, is-welcome class, and related effects deleted.
 *   - B2: Typing sound (keyboard-typing.mp3) removed entirely. Audio element,
 *         primer logic, typingAudioRef, typingAudioPrimedRef all deleted.
 *         typewriterDoneRef retained — still used by typewriter visual logic.
 *   - B3: "Thinking" text + animated dots on AI processing. Immediate on submit.
 *         Replaces the dots-only thinking bubble. aria-live="polite" on thread.
 *   - B4: Step 3 newsletter redesigned — radio Yes/No replacing checkbox.
 *         Default Yes pre-selected. Question label matches email/name label size.
 *         submitTranscript now receives 'yes' | 'no' string.
 *   - B5: Email persisted in localStorage (pf-help-transcript-email).
 *         Pre-fills Step 1; jumps to Step 2 if stored. Edit pill to clear.
 *   - B6: AI chat system prompt overhauled — softened reflex, technique answers,
 *         conversational greetings, tiered response hierarchy.
 *
 * Vera C4 strings (verbatim — do not paraphrase):
 *   Placeholder: "Start typing your question…"
 *   Turn 2+ placeholder: "Ask another question"
 *   Session disclosure: "This chat remembers our conversation in your browser, so
 *     you can pick up where you left off. Clear chat to start over."
 *   Clear confirm: "Clear this conversation? You'll start fresh next time."
 *   Email transcript offer: "Email me this conversation"
 *   Transcript label: "Where should we send it?"
 *   Transcript button: "Send transcript"
 *   Transcript success: "Sent to [email]. If it's not there in a couple of minutes, check spam."
 *
 * Welcome message (Vera C7 — verbatim):
 *   "Hi, I'm AI assistant Eleanor. I'm here to answer your questions about Piano Fantasy
 *   — membership, access, how lessons work, billing, and more. What would you like to know?"
 *
 * Sound asset:
 *   /public/sounds/chat-plop.mp3 — retained on disk, NOT used.
 *   keyboard-typing.mp3 — DELETED (Cycle 13 B2).
 *
 * Added: Vela, feat/help-page-migration-2026-05-12 (Cycle 4)
 * Updated: Vela, feat/help-page-migration-2026-05-12 (Cycle 13)
 * Governance: SOP-WEBSITE-02, SOP-WEBSITE-03, SOP-BRAND-01
 */

import { useState, useRef, useEffect, useCallback, Fragment } from 'react';
import Link from 'next/link';
import { useMailcheck } from '../../../hooks/useMailcheck';
import OrbitSpinner from '@/components/OrbitSpinner';
import { answerSegments } from '@/lib/help-links.mjs';

// ── Types ──────────────────────────────────────────────────────────────────

interface Citation {
  sourceTitle: string;
  sourceUrl: string;
  relevantQuote?: string;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  citations?: Citation[];
  isOutOfScope?: boolean;
  isError?: boolean;
}

type SendStatus = 'idle' | 'sending' | 'error';

// Transcript form state — 3-step progressive disclosure
type TranscriptStatus =
  | 'hidden'
  | 'offer'
  | 'step1'      // email entry
  | 'step2'      // name entry
  | 'step3'      // newsletter opt-in (conditional)
  | 'submitting'
  | 'sent'
  | 'error';

// ── Constants ──────────────────────────────────────────────────────────────

const STORAGE_KEY = 'pf-help-chat-v1';
const EMAIL_STORAGE_KEY = 'pf-help-transcript-email'; // B5: persists email across sessions

// B3 (Cycle 15): Turnstile site key — injected at build time.
// When unset the widget is skipped entirely; the form falls back to IP rate-limit-only.
const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? '';
const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';
const TURNSTILE_WIDGET_ID = 'cf-turnstile-widget';

// Typewriter: 35ms/char.
const TYPEWRITER_CHAR_MS = 35;

const WELCOME_MESSAGE =
  "Hi, I'm AI assistant Eleanor. I'm here to answer your questions about Piano Fantasy — membership, access, how lessons work, billing, and more. What would you like to know?";

/**
 * Starter questions (Hazel 2026-09-22, approved by Eleanor — verbatim).
 *
 * ONE set, shown unchanged on every page that mounts the chat. Deliberately NOT
 * varied per surface: Hazel checked these four against all nine listed pages and
 * none of them references "this book", "this page" or any single surface, so a
 * per-page variant would only create drift. The chat opened with a welcome line
 * and an empty box before this, which left the visitor to invent a question.
 */
const CHAT_STARTERS = [
  'Am I at the right level to start?',
  "What's actually included if I join?",
  'How do the lessons work?',
  "Can I cancel if it's not for me?",
] as const;

// Vera C4 + C7 + C12 strings — verbatim. Do not paraphrase.
const STRINGS = {
  startersLabel: 'Or start with one of these:',
  welcomeMessage: WELCOME_MESSAGE,
  placeholderTurn1: 'Start typing your question…',
  placeholderTurnN: 'Ask another question',
  sessionDisclosure:
    'This chat remembers our conversation in your browser, so you can pick up where you left off. Clear chat to start over.',
  clearConfirm: "Clear this conversation? You'll start fresh next time.",
  transcriptOffer: 'Email me this conversation',
  transcriptLabel: 'Where should we send it?',
  transcriptButton: 'Send transcript',
  transcriptSuccess: (email: string) =>
    `Sent to ${email}. If it's not there in a couple of minutes, check spam.`,
  newsletterQuestion: 'Would you like our weekly piano tips by email?',
  newsletterYes: 'Yes, send me weekly piano tips',
  newsletterNo: 'No, just the transcript',
  noAnswerTurn1:
    "We don't have a clear answer for that in our help library yet. The best next step is to reach out — Lars and the team read every message.",
  noAnswerTurnN:
    "I don't have a clear answer for that one in our help library. You can try rephrasing, ask something else, or reach out to Lars and the team directly — we read every message.",
  offCorpus:
    "That's outside what I can help with here — I only answer questions about Piano Fantasy.",
  systemError:
    "The chat isn't responding right now. Please try again in a moment, or reach out to the team directly.",
  rateLimited:
    "You've sent a lot of questions — wait a moment before asking again, or contact support directly.",
  unavailable:
    'AI chat is being set up — please use the FAQ below or contact support.',
  aiDisclosure: 'AI-generated answer, drawn from our help library.',
  aiBadgeLabel: 'Eleanor AI Assistant',
} as const;

// ── localStorage helpers ───────────────────────────────────────────────────

function loadStoredMessages(): ChatMessage[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed as ChatMessage[];
  } catch {
    // Corrupted — start fresh
  }
  return [];
}

function saveMessages(messages: ChatMessage[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-20)));
    // NOTE: pf-chat-sync CustomEvent was removed here (2026-05-18 loop fix).
    // That event created an infinite loop: save → dispatch → onSameTabSync →
    // setMessages(loadStoredMessages()) → new array ref → persist effect →
    // save → dispatch → ∞. Pegged the main thread causing Chrome "excessive
    // resources" warning, scroll-snap-back, and unclickable elements on /contact.
    // Cross-tab sync is handled by the native `storage` event (fires in OTHER tabs).
    // Same-tab sync between embedded + floating instances is a separate, later task.
  } catch {
    // Quota exceeded or private browsing — ignore
  }
}

function clearStoredMessages(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore
  }
}

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

// ── Email validator ────────────────────────────────────────────────────────
function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

// ── Name splitter: first whitespace → firstName / lastName ─────────────────
function splitName(fullName: string): { firstName: string; lastName: string } {
  const t = fullName.trim();
  const idx = t.indexOf(' ');
  if (idx === -1) return { firstName: t, lastName: '' };
  return { firstName: t.slice(0, idx), lastName: t.slice(idx + 1) };
}

// ── SendArrowIcon inline SVG ───────────────────────────────────────────────
function SendArrowIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M5 12h14M13 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ── TranscriptStep1Form — sub-component so useMailcheck mounts with the input ──
// useMailcheck runs document.getElementById() in a useEffect. If the hook lives in the
// parent (which mounts before step1 is active), the effect finds null and exits early.
// Moving the hook into this sub-component guarantees the effect runs AFTER the input
// element is in the DOM — because the component itself mounts when step1 becomes active.
interface TranscriptStep1FormProps {
  email: string;
  emailError: string;
  onEmailChange: (v: string) => void;
  onClearEmailError: () => void;
  onNext: () => void;
  onCancel: () => void;
  emailRef: React.RefObject<HTMLInputElement | null>;
}

function TranscriptStep1Form({
  email,
  emailError,
  onEmailChange,
  onClearEmailError,
  onNext,
  onCancel,
  emailRef,
}: TranscriptStep1FormProps) {
  // Hook lives here: mounts exactly when step1 renders → element exists in DOM
  const { checkOnSubmit } = useMailcheck({
    emailInputId: 'help-transcript-email',
    suggestionDivId: 'help-transcript-email-suggestion',
  });

  const handleNext = () => {
    // Mailcheck gate — show suggestion and block if typo detected
    if (checkOnSubmit()) return;
    onNext();
  };

  return (
    <div className="help-chat__transcript-form">
      <label htmlFor="help-transcript-email" className="help-chat__transcript-label">
        Where should we send it?
      </label>
      <div className="help-chat__transcript-input-row">
        <div className="help-chat__transcript-input-wrap">
          <input
            ref={emailRef as React.RefObject<HTMLInputElement>}
            id="help-transcript-email"
            type="email"
            className={`help-chat__transcript-email-input${emailError ? ' help-chat__transcript-email-input--error' : ''}`}
            value={email}
            onChange={e => {
              onEmailChange(e.target.value);
              if (emailError) onClearEmailError();
            }}
            placeholder="your@email.com"
            autoComplete="email"
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); handleNext(); }
            }}
            aria-describedby={emailError ? 'help-transcript-email-error' : 'help-transcript-email-suggestion'}
            aria-invalid={emailError ? 'true' : undefined}
          />
          {/* Mailcheck suggestion slot — ID MUST match useMailcheck config above */}
          <div
            id="help-transcript-email-suggestion"
            className="mailcheck-suggestion help-chat__mailcheck-suggestion"
            aria-live="polite"
          />
          {emailError && (
            <p id="help-transcript-email-error" className="help-chat__transcript-field-error" role="alert">
              {emailError}
            </p>
          )}
        </div>
        <button type="button" className="help-chat__transcript-next-btn" onClick={handleNext}>
          Next
        </button>
      </div>
      <button type="button" className="help-chat__transcript-dismiss" onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────

export default function HelpChatClient({ isAvailable = true }: { isAvailable?: boolean }) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sendStatus, setSendStatus] = useState<SendStatus>('idle');
  const [transcriptStatus, setTranscriptStatus] = useState<TranscriptStatus>('hidden');
  const [hydrated, setHydrated] = useState(false);

  // Transcript form fields
  const [transcriptEmail, setTranscriptEmail] = useState('');
  const [transcriptName, setTranscriptName] = useState('');
  // B4: radio Yes/No — default 'yes'
  const [newsletterChoice, setNewsletterChoice] = useState<'yes' | 'no'>('yes');
  const [shouldAskNewsletter, setShouldAskNewsletter] = useState(true);
  const [transcriptSentTo, setTranscriptSentTo] = useState('');
  // B5: whether email was pre-filled from localStorage
  const [emailFromStorage, setEmailFromStorage] = useState(false);

  // Validation errors
  const [emailError, setEmailError] = useState('');
  const [nameError, setNameError] = useState('');

  // Typewriter display
  const [displayedWelcome, setDisplayedWelcome] = useState('');
  const typewriterDoneRef = useRef(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const liveRegionRef = useRef<HTMLDivElement>(null);
  const transcriptEmailRef = useRef<HTMLInputElement>(null);
  const transcriptNameRef = useRef<HTMLInputElement>(null);
  const welcomeBubbleRef = useRef<HTMLDivElement>(null);
  // B3 (Cycle 15): Turnstile widget ref for token capture
  const turnstileWidgetRef = useRef<HTMLDivElement>(null);
  // Cycle 84 Bug A: store the widget ID returned by turnstile.render() so
  // getResponse(widgetId) and reset(widgetId) target the correct instance.
  const turnstileWidgetIdRef = useRef<string | null>(null);

  // ── Hydration ──
  useEffect(() => {
    const stored = loadStoredMessages();
    if (stored.length > 0) setMessages(stored);

    // B5: pre-fill email from localStorage if available
    try {
      const storedEmail = localStorage.getItem(EMAIL_STORAGE_KEY);
      if (storedEmail) {
        setTranscriptEmail(storedEmail);
        setEmailFromStorage(true);
      }
    } catch {
      // private browsing or quota — ignore
    }

    setHydrated(true);
  }, []);

  // ── Cross-tab chat sync (2026-05-18).
  // When another tab writes STORAGE_KEY, the native `storage` event fires here
  // and re-hydrates messages from the updated value.
  // NOTE: same-tab sync via pf-chat-sync CustomEvent was deliberately removed —
  // it created an infinite feedback loop (save → dispatch → setMessages → re-render
  // → persist effect → save again → ∞) that pegged the CPU on /contact.
  // Same-tab sync between embedded + floating instances is a separate future task.
  useEffect(() => {
    const onStorageChange = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      const updated = loadStoredMessages();
      setMessages(updated);
    };
    window.addEventListener('storage', onStorageChange);
    return () => window.removeEventListener('storage', onStorageChange);
  }, []);

  // ── B3 (Cycle 15): Inject Turnstile script — only when site key is provisioned ──
  // Script is injected once on mount; idempotent (checks for existing script tag).
  // When NEXT_PUBLIC_TURNSTILE_SITE_KEY is not set, this effect is a no-op.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return; // fail-open: no key → no script
    if (document.getElementById(TURNSTILE_SCRIPT_ID)) return; // already injected
    const script = document.createElement('script');
    script.id = TURNSTILE_SCRIPT_ID;
    // data-explicit-render prevents the script from auto-scanning the DOM on load.
    // Cycle 84 Bug A root-cause: the auto-scan ran before the widget <div> was in the
    // DOM (transcriptStatus was still 'hidden'), so the widget was never initialised
    // and getResponse() always returned ''. With explicit render mode, we call
    // window.turnstile.render() ourselves once the element mounts (effect below).
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, []);

  // ── Cycle 84 Bug A: Explicit Turnstile widget render ──
  // Runs when transcriptStatus leaves 'hidden' (= widget div just appeared in DOM).
  // Calls window.turnstile.render() on the widget element if the Turnstile script
  // is already loaded; polls briefly if it hasn't loaded yet (script is async).
  // Stores the returned widgetId in turnstileWidgetIdRef for getResponse() + reset().
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return; // no key → skip
    if (transcriptStatus === 'hidden') return; // widget not yet in DOM
    if (turnstileWidgetIdRef.current !== null) return; // already rendered

    const el = turnstileWidgetRef.current;
    if (!el) return;

    function tryRender() {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const turnstile = (window as any).turnstile;
      if (turnstile && typeof turnstile.render === 'function' && el) {
        // render() returns the widget ID string; needed for getResponse(id) and reset(id).
        turnstileWidgetIdRef.current = turnstile.render(el, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: 'light',
        }) as string;
      }
    }

    // If the script is already loaded, render immediately.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window as any).turnstile) {
      tryRender();
      return;
    }

    // Script not yet loaded — poll until it is (max ~3s, 10 × 300ms).
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((window as any).turnstile) {
        clearInterval(interval);
        tryRender();
      } else if (attempts >= 10) {
        clearInterval(interval); // give up — fail-open (no token → 403)
      }
    }, 300);

    return () => clearInterval(interval);
  }, [transcriptStatus]); // re-run when the widget enters / leaves the DOM

  // ── Typewriter animation on welcome message ──
  // B2: Audio removed entirely. Visual typewriter only.
  // prefers-reduced-motion: shows full text immediately.
  useEffect(() => {
    if (!hydrated) return;
    if (typewriterDoneRef.current) return;

    if (prefersReducedMotion()) {
      setDisplayedWelcome(WELCOME_MESSAGE);
      typewriterDoneRef.current = true;
      return;
    }

    let charIndex = 0;
    let tickId: ReturnType<typeof setInterval> | null = null;

    const startId = setTimeout(() => {
      tickId = setInterval(() => {
        charIndex++;
        setDisplayedWelcome(WELCOME_MESSAGE.slice(0, charIndex));

        if (charIndex >= WELCOME_MESSAGE.length) {
          if (tickId !== null) clearInterval(tickId);
          typewriterDoneRef.current = true;
        }
      }, TYPEWRITER_CHAR_MS);
    }, 400);

    return () => {
      clearTimeout(startId);
      if (tickId !== null) clearInterval(tickId);
    };
  // Run once after hydration
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated]);

  // ── Persist to localStorage ──
  useEffect(() => {
    if (!hydrated) return;
    if (messages.length > 0) saveMessages(messages);
  }, [messages, hydrated]);

  // ── Auto-scroll ──
  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [messages]);

  // ── Auto-resize textarea ──
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
  }, [input]);

  // ── Show transcript offer after first AI reply ──
  useEffect(() => {
    const hasAiReply = messages.some(m => m.role === 'assistant' && !m.isError);
    if (hasAiReply && transcriptStatus === 'hidden') setTranscriptStatus('offer');
  }, [messages, transcriptStatus]);

  // ── Screen reader announce ──
  const announce = useCallback((msg: string) => {
    if (!liveRegionRef.current) return;
    liveRegionRef.current.textContent = '';
    setTimeout(() => {
      if (liveRegionRef.current) liveRegionRef.current.textContent = msg;
    }, 50);
  }, []);

  // ── Send message ──
  /**
   * Send a message. With no argument it sends whatever is in the textarea;
   * `presetText` is how a starter question sends itself without a round trip
   * through `input` state (setInput is async, so a starter that set state and
   * then called this would send the PREVIOUS value, or an empty string on the
   * first tap).
   */
  const sendMessage = useCallback(async (presetText?: string) => {
    const trimmed = (presetText ?? input).trim();
    if (!trimmed || trimmed.length < 3 || sendStatus === 'sending' || !isAvailable) return;

    const userMsg: ChatMessage = { role: 'user', content: trimmed };
    const updatedMessages = [...messages, userMsg];

    setMessages(updatedMessages);
    setInput('');
    setSendStatus('sending');
    announce('Thinking…');

    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    try {
      const apiMessages = updatedMessages.map(m => ({ role: m.role, content: m.content }));

      const res = await fetch('/api/help-search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: apiMessages }),
      });

      if (res.status === 429) {
        setMessages(prev => [...prev, { role: 'assistant', content: STRINGS.rateLimited, isError: true }]);
        announce('Rate limited — please wait.');
        setSendStatus('error');
        return;
      }

      // 503 = the route's documented "key missing" state (route.ts returns
      // { unavailable: true } with status 503). It must degrade kindly to the
      // "being set up" message, NOT to the generic system-error message — this
      // check has to precede the generic !res.ok branch below.
      if (res.status === 503) {
        setMessages(prev => [...prev, { role: 'assistant', content: STRINGS.unavailable, isError: true }]);
        announce('AI chat is not available right now.');
        setSendStatus('error');
        return;
      }

      if (!res.ok) {
        setMessages(prev => [...prev, { role: 'assistant', content: STRINGS.systemError, isError: true }]);
        announce('An error occurred.');
        setSendStatus('error');
        return;
      }

      const data = await res.json() as {
        answer: string;
        citations: Citation[];
        isOutOfScope: boolean;
        noAnswerTurn?: boolean;
        unavailable?: boolean;
      };

      if (data.unavailable) {
        setMessages(prev => [...prev, { role: 'assistant', content: STRINGS.unavailable, isError: true }]);
        setSendStatus('error');
        return;
      }

      const isMultiTurnNow = updatedMessages.filter(m => m.role === 'user').length > 1;

      const aiMsg: ChatMessage = {
        role: 'assistant',
        content: data.isOutOfScope
          ? data.noAnswerTurn
            ? STRINGS.noAnswerTurnN
            : data.answer.includes('outside what I can help') || data.answer.includes('only answer questions about Piano Fantasy')
              ? STRINGS.offCorpus
              : isMultiTurnNow ? STRINGS.noAnswerTurnN : STRINGS.noAnswerTurn1
          : data.answer,
        citations: data.citations?.length > 0 ? data.citations : undefined,
        isOutOfScope: data.isOutOfScope,
      };

      setMessages(prev => [...prev, aiMsg]);
      announce(data.isOutOfScope ? 'No answer found.' : 'Answer received.');
      setSendStatus('idle');
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: STRINGS.systemError, isError: true }]);
      announce('An error occurred.');
      setSendStatus('error');
    } finally {
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [input, messages, sendStatus, isAvailable, announce]);

  // ── Clear chat ──
  const handleClear = () => {
    if (!window.confirm(STRINGS.clearConfirm)) return;
    setMessages([]);
    setTranscriptStatus('hidden');
    // B5: do NOT clear transcriptEmail or emailFromStorage — user's email persists
    setTranscriptName('');
    setNewsletterChoice('yes'); // B4: reset to default Yes
    setShouldAskNewsletter(true);
    setTranscriptSentTo('');
    setEmailError('');
    setNameError('');
    clearStoredMessages();
    setSendStatus('idle');
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  // ── Keyboard: Enter sends, Shift+Enter newline ──
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void sendMessage();
    }
  };

  // ── Transcript Step 1 → Step 2 ──
  // The mailcheck gate runs inside TranscriptStep1Form's handleNext, BEFORE this is called.
  // This function only runs if mailcheck passed (no suggestion shown) AND email is valid.
  const handleStep1Next = () => {
    const email = transcriptEmail.trim();

    if (!email || !isValidEmail(email)) {
      setEmailError('Please enter your email');
      return;
    }
    setEmailError('');

    // B5: persist email to localStorage so it pre-fills next time
    try {
      localStorage.setItem(EMAIL_STORAGE_KEY, email);
      setEmailFromStorage(true);
    } catch {
      // quota or private browsing — ignore
    }

    setTranscriptStatus('step2');
    setTimeout(() => transcriptNameRef.current?.focus(), 50);
  };

  // ── Transcript Step 2 → Step 3 or Submit ──
  const handleStep2Next = async () => {
    const name = transcriptName.trim();
    if (!name) {
      setNameError('Please enter your name');
      return;
    }
    setNameError('');

    // Background check: does this email already have newsletter preferences set?
    let ask = true;
    try {
      const res = await fetch('/api/help-search/check-email-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: transcriptEmail.trim() }),
      });
      if (res.ok) {
        const data = await res.json() as { shouldAskNewsletter: boolean };
        ask = data.shouldAskNewsletter;
      }
    } catch {
      ask = true; // fail-open: show newsletter question when GHL unreachable
    }

    setShouldAskNewsletter(ask);

    if (ask) {
      setTranscriptStatus('step3');
    } else {
      // Skip step 3 — submit immediately, no newsletter change
      await submitTranscript('no');
    }
  };

  // ── B3 (Cycle 15) + Cycle 84 Bug A: Get Turnstile token from widget (if provisioned) ──
  // Cloudflare Turnstile writes the solved token onto the widget element via
  // window.turnstile.getResponse(widgetId). Falls back to an empty string when
  // Turnstile is not loaded — the server will accept that (fail-open, rate-limit-only).
  // Cycle 84: pass turnstileWidgetIdRef.current so we target the right widget instance
  // (required when using explicit render mode — no-arg getResponse() returns undefined).
  const getTurnstileToken = (): string => {
    if (!TURNSTILE_SITE_KEY) return ''; // no key → skip
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const turnstile = (window as any).turnstile;
      if (turnstile && typeof turnstile.getResponse === 'function') {
        const widgetId = turnstileWidgetIdRef.current;
        return (widgetId ? turnstile.getResponse(widgetId) : turnstile.getResponse()) ?? '';
      }
    } catch {
      // Turnstile not loaded yet — fail-open
    }
    return '';
  };

  // ── Transcript final submit ──
  // B4: receives newsletterChoice 'yes' | 'no' (replaces boolean withNewsletter)
  const submitTranscript = async (choice: 'yes' | 'no') => {
    const email = transcriptEmail.trim();
    const name = transcriptName.trim();

    setTranscriptStatus('submitting');

    const conversationPayload = messages
      .filter(m => m.role === 'user' || (m.role === 'assistant' && !m.isError))
      .map(m => ({ role: m.role, content: m.content }));

    const { firstName, lastName } = splitName(name);

    // B3 (Cycle 15): capture Turnstile token (empty string if keys not provisioned)
    const turnstileToken = getTurnstileToken();

    try {
      const res = await fetch('/api/help-search/email-transcript', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          firstName,
          lastName,
          newsletterChoice: choice,
          conversation: conversationPayload,
          ...(turnstileToken ? { turnstileToken } : {}),
        }),
      });

      if (res.ok) {
        setTranscriptSentTo(email);
        setTranscriptStatus('sent');
        // B3 (Cycle 16): Reset Turnstile so a subsequent transcript send in the
        // same session gets a fresh token. Tokens are single-use; without reset
        // the server would 403 the second submit. No-op when the widget is not
        // provisioned (NEXT_PUBLIC_TURNSTILE_SITE_KEY unset).
        if (TURNSTILE_SITE_KEY) {
          try {
            const turnstile = (window as { turnstile?: { reset?: (id?: string | HTMLElement) => void } }).turnstile;
            // Cycle 84: pass widgetId for explicit-render mode; fallback to no-arg for safety.
            const widgetId = turnstileWidgetIdRef.current;
            if (widgetId) {
              turnstile?.reset?.(widgetId);
            } else {
              turnstile?.reset?.();
            }
            // Clear stored ID so the render effect re-renders a fresh widget.
            turnstileWidgetIdRef.current = null;
          } catch {
            // Non-fatal — at worst the next submit gets a 403 and the user retries.
          }
        }
      } else {
        setTranscriptStatus('error');
      }
    } catch {
      setTranscriptStatus('error');
    }
  };

  // ── Unavailable ──
  if (!isAvailable) {
    return (
      <div className="help-chat__unavailable" role="status">
        <p className="help-chat__unavailable-msg">{STRINGS.unavailable}</p>
      </div>
    );
  }

  const hasMessages = messages.length > 0;
  const isMultiTurn = messages.filter(m => m.role === 'user').length >= 1;
  const isSending = sendStatus === 'sending';
  const inputHasContent = input.trim().length >= 3;

  // Welcome text: typewriter progress until done, then full string
  const welcomeText = typewriterDoneRef.current ? WELCOME_MESSAGE : (displayedWelcome || '');

  return (
    <div className="help-chat">
      {/* Screen-reader live region */}
      <div ref={liveRegionRef} aria-live="polite" role="status" className="sr-only" />

      {/* ── Cream chat container ── */}
      <div
        className="help-chat__container"
        aria-label="Piano Fantasy help chat"
        aria-busy={isSending}
      >

        {/* ── Header bar ── */}
        <div className="help-chat__header">
          <div className="help-chat__header-left">
            <span className="help-chat__ai-badge" aria-hidden="true">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                className="help-chat__badge-icon"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              <span className="help-chat__badge-label">Ask Piano Fantasy</span>
            </span>
            {hasMessages && (
              <button
                type="button"
                className="help-chat__clear-btn"
                onClick={handleClear}
                aria-label="Clear conversation and start over"
              >
                Clear chat
              </button>
            )}
          </div>
        </div>

        {/* ── AI disclosure strip — §14.10c (brand-kit v1.53).
             Relocated from header-right. Small, centered, text-dark-muted, border-bottom.
             Sits immediately after header divider and before first assistant message.
             Verbatim string (Vera C4): "AI-generated answer, drawn from our help library." */}
        <div className="help-chat__disclosure-strip" aria-label={STRINGS.aiDisclosure}>
          {STRINGS.aiDisclosure}
        </div>

        {/* ── Conversation thread ── */}
        <div className="help-chat__thread" role="log" aria-label="Conversation" aria-live="polite">

          {/* Welcome bubble — typewriter only, no bounce animation (B1) */}
          {hydrated && (
            <div
              ref={welcomeBubbleRef}
              className="help-chat__bubble help-chat__bubble--ai help-chat__bubble--welcome"
            >
              <span className="help-chat__bubble-attr" aria-hidden="true">
                {STRINGS.aiBadgeLabel}
              </span>
              <p className="help-chat__bubble-text">
                {welcomeText}
                {!typewriterDoneRef.current && displayedWelcome.length > 0 && (
                  <span className="help-chat__caret" aria-hidden="true" />
                )}
              </p>
            </div>
          )}

          {/* Starter questions — first turn only, then they are gone for good.
              Real <button>s in a <ul>, so they are tab-reachable and announced
              as a list; the visible focus ring is .help-chat__starter:focus-visible. */}
          {hydrated && !hasMessages && isAvailable && (
            <div className="help-chat__starters">
              <p className="help-chat__starters-label" id="help-chat-starters-label">
                {STRINGS.startersLabel}
              </p>
              <ul className="help-chat__starters-list" aria-labelledby="help-chat-starters-label">
                {CHAT_STARTERS.map(question => (
                  <li key={question}>
                    <button
                      type="button"
                      className="help-chat__starter"
                      disabled={sendStatus === 'sending'}
                      onClick={() => void sendMessage(question)}
                    >
                      {question}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Conversation messages */}
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`help-chat__bubble help-chat__bubble--${msg.role === 'assistant' ? 'ai' : msg.role}${msg.isError ? ' help-chat__bubble--error' : ''}${msg.isOutOfScope ? ' help-chat__bubble--out-of-scope' : ''}`}
            >
              {msg.role === 'assistant' && (
                <span className="help-chat__bubble-attr" aria-hidden="true">
                  {STRINGS.aiBadgeLabel}
                </span>
              )}
              <p className="help-chat__bubble-text">
                {msg.role === 'assistant'
                  ? answerSegments(msg.content).map((seg, si) =>
                      seg.type === 'link' ? (
                        // Answer links (2026-09-15): the server already limited them to the
                        // verified list. Site convention for an in-text link on cream.
                        <a key={si} href={seg.href} className="text-link text-link--on-cream">
                          {seg.text}
                        </a>
                      ) : (
                        <Fragment key={si}>{seg.text}</Fragment>
                      ),
                    )
                  : msg.content}
              </p>

              {msg.role === 'assistant' && msg.citations && msg.citations.length > 0 && (
                <div className="help-chat__citations">
                  <span className="help-chat__citations-separator" aria-hidden="true" />
                  <div className="help-chat__citations-pills">
                    {msg.citations.map((c, ci) => (
                      <a key={ci} href={c.sourceUrl} className="help-chat__citation-pill">
                        {c.sourceTitle}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {msg.role === 'assistant' && msg.isOutOfScope && !msg.isError && (
                <div className="help-chat__cta-row">
                  <Link href="/contact" className="help-chat__contact-cta">
                    Contact Support
                  </Link>
                </div>
              )}
            </div>
          ))}

          {/* Thinking indicator — B3: text + animated dots, immediate on submit */}
          {isSending && (
            <div
              className="help-chat__bubble help-chat__bubble--ai help-chat__bubble--thinking"
              role="status"
              aria-label="Thinking"
            >
              <OrbitSpinner size={16} cream decorative />
              <span className="help-chat__thinking-text">Thinking…</span>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* ── Session disclosure ── */}
        {hasMessages && (
          <p className="help-chat__session-note">{STRINGS.sessionDisclosure}</p>
        )}

        {/* ── Email transcript — 3-step progressive disclosure ── */}
        {transcriptStatus !== 'hidden' && (
          <div className="help-chat__transcript-zone">

            {/* Offer button */}
            {transcriptStatus === 'offer' && (
              <button
                type="button"
                className="help-chat__transcript-offer-btn"
                onClick={() => {
                  // B5: if email is from storage, skip Step 1 → go directly to Step 2
                  if (emailFromStorage && transcriptEmail) {
                    setTranscriptStatus('step2');
                    setTimeout(() => transcriptNameRef.current?.focus(), 50);
                  } else {
                    setTranscriptStatus('step1');
                    setTimeout(() => transcriptEmailRef.current?.focus(), 50);
                  }
                }}
              >
                {STRINGS.transcriptOffer}
              </button>
            )}

            {/* Step 1 — Email (sub-component so useMailcheck mounts with the input) */}
            {transcriptStatus === 'step1' && (
              <TranscriptStep1Form
                email={transcriptEmail}
                emailError={emailError}
                onEmailChange={setTranscriptEmail}
                onClearEmailError={() => setEmailError('')}
                onNext={handleStep1Next}
                onCancel={() => setTranscriptStatus('offer')}
                emailRef={transcriptEmailRef}
              />
            )}

            {/* B5: Edit pill shown at top of Step 2+ when email came from storage */}
            {(transcriptStatus === 'step2' || transcriptStatus === 'step3') && emailFromStorage && transcriptEmail && (
              <div className="help-chat__transcript-confirmed-row">
                <span className="help-chat__transcript-confirmed-pill" title={transcriptEmail}>
                  {transcriptEmail}
                </span>
                <button
                  type="button"
                  className="help-chat__transcript-edit-btn"
                  onClick={() => {
                    // Clear stored email; return to Step 1 with field empty
                    try { localStorage.removeItem(EMAIL_STORAGE_KEY); } catch { /* ignore */ }
                    setTranscriptEmail('');
                    setEmailFromStorage(false);
                    setTranscriptStatus('step1');
                    setTimeout(() => transcriptEmailRef.current?.focus(), 50);
                  }}
                >
                  Edit
                </button>
              </div>
            )}

            {/* Step 2 — Name */}
            {transcriptStatus === 'step2' && (
              <div className="help-chat__transcript-form">
                <label htmlFor="help-transcript-name" className="help-chat__transcript-label">
                  What&apos;s your name?
                </label>
                <div className="help-chat__transcript-input-row">
                  <div className="help-chat__transcript-input-wrap">
                    <input
                      ref={transcriptNameRef}
                      id="help-transcript-name"
                      type="text"
                      className={`help-chat__transcript-email-input${nameError ? ' help-chat__transcript-email-input--error' : ''}`}
                      value={transcriptName}
                      onChange={e => {
                        setTranscriptName(e.target.value);
                        if (nameError) setNameError('');
                      }}
                      placeholder="Your name"
                      autoComplete="name"
                      onKeyDown={e => {
                        if (e.key === 'Enter') { e.preventDefault(); void handleStep2Next(); }
                      }}
                      aria-describedby={nameError ? 'help-transcript-name-error' : undefined}
                      aria-invalid={nameError ? 'true' : undefined}
                    />
                    {nameError && (
                      <p id="help-transcript-name-error" className="help-chat__transcript-field-error" role="alert">
                        {nameError}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    className="help-chat__transcript-next-btn"
                    onClick={() => void handleStep2Next()}
                  >
                    Next
                  </button>
                </div>
                <button
                  type="button"
                  className="help-chat__transcript-dismiss"
                  onClick={() => setTranscriptStatus('step1')}
                >
                  Back
                </button>
              </div>
            )}

            {/* Step 3 — Newsletter radio Yes/No (B4 — replaces checkbox) */}
            {transcriptStatus === 'step3' && shouldAskNewsletter && (
              <div className="help-chat__transcript-form">
                {/* Question label — same font/size as email/name label (var(--text-body-mobile) 600) */}
                <p className="help-chat__transcript-newsletter-question">
                  {STRINGS.newsletterQuestion}
                </p>
                <div className="help-chat__transcript-radio-group" role="radiogroup" aria-label={STRINGS.newsletterQuestion}>
                  <label className="help-chat__transcript-radio-label">
                    <input
                      type="radio"
                      name="newsletterChoice"
                      value="yes"
                      className="help-chat__transcript-radio"
                      checked={newsletterChoice === 'yes'}
                      onChange={() => setNewsletterChoice('yes')}
                      required
                    />
                    {STRINGS.newsletterYes}
                  </label>
                  <label className="help-chat__transcript-radio-label">
                    <input
                      type="radio"
                      name="newsletterChoice"
                      value="no"
                      className="help-chat__transcript-radio"
                      checked={newsletterChoice === 'no'}
                      onChange={() => setNewsletterChoice('no')}
                    />
                    {STRINGS.newsletterNo}
                  </label>
                </div>
                <button
                  type="button"
                  className="help-chat__transcript-send-btn"
                  onClick={() => void submitTranscript(newsletterChoice)}
                >
                  {STRINGS.transcriptButton}
                </button>
                <button
                  type="button"
                  className="help-chat__transcript-dismiss"
                  onClick={() => setTranscriptStatus('step2')}
                >
                  Back
                </button>
              </div>
            )}

            {/* Submitting */}
            {transcriptStatus === 'submitting' && (
              <p className="help-chat__transcript-note" aria-live="polite">Sending…</p>
            )}

            {/* Error */}
            {transcriptStatus === 'error' && (
              <div className="help-chat__transcript-form">
                <p className="help-chat__transcript-error" role="alert">
                  Could not send — please try again or contact support directly.
                </p>
                <button
                  type="button"
                  className="help-chat__transcript-dismiss"
                  onClick={() => setTranscriptStatus('offer')}
                >
                  Try again
                </button>
              </div>
            )}

            {/* Sent */}
            {transcriptStatus === 'sent' && (
              <p className="help-chat__transcript-success">
                {STRINGS.transcriptSuccess(transcriptSentTo)}
              </p>
            )}

            {/* B3 (Cycle 15): Invisible Turnstile widget — only rendered when site key is provisioned.
                Widget mounts here so it initialises while the user fills the form steps.
                No visible CAPTCHA puzzle; CF validates silently in the background. */}
            {TURNSTILE_SITE_KEY && (
              <div
                ref={turnstileWidgetRef}
                id={TURNSTILE_WIDGET_ID}
                className="cf-turnstile"
                data-sitekey={TURNSTILE_SITE_KEY}
                data-theme="light"
                style={{
                  position: 'absolute',
                  left: '-10000px',
                  top: 'auto',
                  width: '1px',
                  height: '1px',
                  overflow: 'hidden',
                }}
                aria-hidden="true"
              />
            )}
          </div>
        )}

        {/* ── Input zone ── */}
        <div className={`help-chat__input-zone${isSending ? ' help-chat__input-zone--loading' : ''}`}>
          <label htmlFor="help-chat-input" className="sr-only">
            {isMultiTurn ? STRINGS.placeholderTurnN : STRINGS.placeholderTurn1}
          </label>
          <div className="help-chat__textarea-wrap">
            <textarea
              ref={textareaRef}
              id="help-chat-input"
              className="help-chat__textarea"
              placeholder={isMultiTurn ? STRINGS.placeholderTurnN : STRINGS.placeholderTurn1}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSending}
              rows={1}
              aria-label={isMultiTurn ? STRINGS.placeholderTurnN : STRINGS.placeholderTurn1}
              autoComplete="off"
            />
            <button
              type="button"
              className={`help-chat__send-btn${inputHasContent && !isSending ? ' help-chat__send-btn--active' : ''}`}
              onClick={() => void sendMessage()}
              disabled={isSending || !inputHasContent}
              aria-label="Send message"
              aria-disabled={isSending || !inputHasContent}
            >
              <SendArrowIcon />
            </button>
          </div>
          <p className="help-chat__input-hint">
            Enter to send <span aria-hidden="true">·</span> Shift+Enter for a new line
          </p>
        </div>

      </div>
    </div>
  );
}
